import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { uploadImage, updateMyProfile, getFileUrl, formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Camera, Save, ArrowLeft, Shield, User as UserIcon, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const STATES = ["Tlaxcala", "Puebla", "CDMX", "Estado de México", "Veracruz", "Hidalgo", "Oaxaca", "Otro"];

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const photoRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    curp: user?.curp || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || 'Tlaxcala',
    birth_date: user?.birth_date || '',
    photo_url: user?.photo_url || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) { nav('/login'); return null; }

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadImage(file);
      set('photo_url', data.path);
      // Save immediately so the banner reflects the new photo
      await updateMyProfile({ photo_url: data.path });
      await checkAuth();
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error('Error al subir foto');
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await updateMyProfile(form);
      await checkAuth();
      toast.success('Perfil actualizado');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const photoSrc = form.photo_url ? (form.photo_url.startsWith('http') ? form.photo_url : getFileUrl(form.photo_url)) : null;
  const isAdmin = user.role === 'admin';

  return (
    <div data-testid="profile-page" className="max-w-6xl mx-auto px-4 py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6">
        <ArrowLeft className="h-3 w-3" /> VOLVER
      </button>

      <div className="mb-8 pb-4 border-b-4 border-[#0B1F40]">
        <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">MI CUENTA</span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>PERFIL</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
        {/* LEFT — Form */}
        <div>
          <div className="bg-white border-2 border-[#0B1F40] p-6 sm:p-8 brutal-shadow">
            {error && <div data-testid="profile-error" className="mb-4 p-3 bg-red-50 border-l-4 border-[#E50B15] text-[#E50B15] text-xs font-bold">{error}</div>}

            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">NOMBRE COMPLETO *</Label>
                  <Input data-testid="profile-name" value={form.name} onChange={e => set('name', e.target.value)}
                    className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10 font-bold" required />
                </div>
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">EMAIL</Label>
                  <Input value={user.email} disabled className="rounded-none border-2 border-[#0B1F40]/10 bg-[#F4F5F7] mt-1 h-10 font-bold text-[#0B1F40]/40" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">TELÉFONO</Label>
                  <Input data-testid="profile-phone" value={form.phone} onChange={e => set('phone', e.target.value)}
                    className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
                </div>
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">FECHA DE NACIMIENTO</Label>
                  <Input data-testid="profile-birth" type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)}
                    className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
                </div>
              </div>

              <div>
                <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CURP</Label>
                <Input data-testid="profile-curp" value={form.curp} onChange={e => set('curp', e.target.value.toUpperCase())}
                  className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10 uppercase" maxLength={18} />
              </div>

              <div>
                <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">DIRECCIÓN</Label>
                <Input data-testid="profile-address" value={form.address} onChange={e => set('address', e.target.value)}
                  className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CIUDAD / MUNICIPIO</Label>
                  <Input data-testid="profile-city" value={form.city} onChange={e => set('city', e.target.value)}
                    className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
                </div>
                <div>
                  <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">ESTADO</Label>
                  <Select value={form.state} onValueChange={v => set('state', v)}>
                    <SelectTrigger data-testid="profile-state" className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-[#0B1F40]">{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <button data-testid="save-profile" type="submit" disabled={saving}
                className="w-full bg-[#E50B15] text-white font-black text-sm h-11 flex items-center justify-center gap-2 brutal-btn-press transition-all mt-2" style={{ boxShadow: '4px 4px 0px #0B1F40' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} GUARDAR CAMBIOS
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT — Photo banner */}
        <aside data-testid="profile-banner" className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white border-2 border-[#0B1F40] overflow-hidden" style={{ boxShadow: '6px 6px 0 #E50B15' }}>
            {/* Header */}
            <div className={`${isAdmin ? 'bg-[#E50B15]' : 'bg-[#0B1F40]'} px-4 py-2 flex items-center gap-1.5`}>
              {isAdmin ? <Shield className="h-3 w-3 text-white" /> : <UserIcon className="h-3 w-3 text-white" />}
              <span className="text-[9px] font-black tracking-[0.25em] uppercase text-white">{isAdmin ? 'ADMINISTRADOR' : 'MI PERFIL'}</span>
            </div>

            {/* Photo */}
            <div className="relative aspect-square bg-[#F4F5F7] overflow-hidden">
              {photoSrc ? (
                <img data-testid="profile-photo" src={photoSrc} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <p className="text-[#0B1F40]/15 text-8xl font-black" style={{ fontFamily: 'Archivo Black' }}>{user.name?.charAt(0) || '?'}</p>
                  <p className="text-[10px] text-[#0B1F40]/30 font-black tracking-[0.2em] uppercase mt-2">SIN FOTO</p>
                </div>
              )}

              {/* Change photo overlay button */}
              <input ref={photoRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
              <button data-testid="change-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploading}
                className="absolute bottom-3 right-3 bg-[#E50B15] text-white text-[9px] font-black h-8 px-3 flex items-center gap-1.5 hover:bg-[#0B1F40] transition-colors"
                style={{ boxShadow: '3px 3px 0 #0B1F40' }}>
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                {photoSrc ? 'CAMBIAR FOTO' : 'SUBIR FOTO'}
              </button>
            </div>

            {/* Info */}
            <div className="p-4 space-y-2">
              <div>
                <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">NOMBRE</p>
                <p className="text-sm font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{user.name}</p>
              </div>
              <div>
                <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">EMAIL</p>
                <p className="text-[10px] font-bold text-[#0B1F40] break-all">{user.email}</p>
              </div>
              {user.user_id_code && (
                <div>
                  <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">ID</p>
                  <p className="text-[11px] font-black text-[#E50B15]">{user.user_id_code}</p>
                </div>
              )}
              <div className="pt-2 border-t border-[#0B1F40]/10">
                <span className={`inline-block text-[8px] font-black tracking-[0.2em] uppercase px-2 py-1 ${isAdmin ? 'bg-[#E50B15] text-white' : 'bg-[#0B1F40] text-white'}`}>
                  {isAdmin ? 'ADMINISTRADOR' : 'PERIODISTA'}
                </span>
              </div>
            </div>

            {/* Credential shortcut (admin only for now, endpoint is admin) */}
            {isAdmin && user._id && (
              <button data-testid="view-my-credential" onClick={() => nav(`/credential/${user._id}`)}
                className="w-full bg-[#0B1F40] text-white text-[10px] font-black tracking-[0.2em] uppercase h-10 flex items-center justify-center gap-2 hover:bg-[#E50B15] transition-colors">
                <CreditCard className="h-3 w-3" /> VER MI CREDENCIAL
              </button>
            )}
          </div>

          <p className="text-center text-[9px] text-[#0B1F40]/30 font-bold tracking-wider mt-4">
            FORMATO JPG / PNG / WEBP — MÁX 10MB
          </p>
        </aside>
      </div>
    </div>
  );
}
